import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'

/**
 * "Is it working?" — the mid-campaign question (docs/campaign-workflow.md).
 *
 * Once the program is running, the question stops being who to talk to and
 * becomes whether the talking is working: how many yes, how many no, how many
 * undecided, how much did we actually get done.
 *
 * TWO KINDS OF NUMBER, kept visibly apart, because mixing them is how a
 * campaign talks itself into believing it has more support than it has:
 *
 *   STANDING — one row per PERSON, from their latest answer. "Supporters" is
 *              this, and someone re-canvassed is still one person.
 *   ACTIVITY — one row per EVENT. "Doors knocked" is this, and a door knocked
 *              twice is two.
 *
 * Form follows the data's job (dataviz): headline counts are stat tiles rather
 * than a bar chart of five numbers, polarity is ONE diverging stacked bar, and
 * the eighteen-way disposition breakdown is a table — past about seven classes,
 * more colours stop helping and a table starts.
 */

interface Totals {
  people: string
  reached: string
  supporters: string
  opposed: string
  events: string
  canvassers: string
  first_contact: string | null
  last_contact: string | null
}

interface SummaryRow {
  disposition: string | null
  label: string | null
  support_level: number | null
  is_contact: boolean | null
  people: string
}

interface MethodRow {
  method: string
  events: string
  people: string
  reached: string
}

interface CanvasserRow {
  canvasser: string
  events: string
  people: string
  reached: string
  supporters: string
  last_worked: string | null
}

const METHOD_LABELS: Record<string, string> = {
  walk: 'Canvass',
  phone: 'Phone',
  text: 'Text',
  mail: 'Mail',
  event: 'Event',
  other: 'Other',
}

const n = (value: string | null | undefined) => Number(value ?? 0)
const fmt = (value: number) => value.toLocaleString()

/**
 * A headline number.
 *
 * A stat tile, not a one-bar bar chart: a single current value has no magnitude
 * to compare against, so the bar would encode nothing the number does not.
 */
function Stat({ value, label, hint }: { value: number; label: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface-raised px-4 py-3">
      <div className="text-2xl font-semibold tabular-nums tracking-tight">{fmt(value)}</div>
      <div className="mt-0.5 text-sm font-medium text-ink">{label}</div>
      {hint ? <div className="text-xs text-ink-muted">{hint}</div> : null}
    </div>
  )
}

export function Progress() {
  const { campaignId } = useSelectedCampaign()

  const totals = useQuery({
    queryKey: ['contact-totals', campaignId],
    queryFn: () => apiGet<Totals>(`/contacts/totals?campaign_id=${campaignId}`),
    enabled: Boolean(campaignId),
  })
  const summary = useQuery({
    queryKey: ['contact-summary', campaignId],
    queryFn: () => apiGet<{ summary: SummaryRow[] }>(`/contacts/summary?campaign_id=${campaignId}`),
    enabled: Boolean(campaignId),
  })
  const methods = useQuery({
    queryKey: ['contact-methods', campaignId],
    queryFn: () => apiGet<{ methods: MethodRow[] }>(`/contacts/by-method?campaign_id=${campaignId}`),
    enabled: Boolean(campaignId),
  })
  const canvassers = useQuery({
    queryKey: ['contact-canvassers', campaignId],
    queryFn: () =>
      apiGet<{ canvassers: CanvasserRow[] }>(`/contacts/by-canvasser?campaign_id=${campaignId}`),
    enabled: Boolean(campaignId),
  })

  const t = totals.data
  const rows = summary.data?.summary ?? []

  const supporters = n(t?.supporters)
  const opposed = n(t?.opposed)
  const people = n(t?.people)
  const reached = n(t?.reached)
  // Undecided is everyone reached who landed on neither pole: refused, language
  // barrier and an explicit "undecided" all sit here. Derived rather than
  // queried so the three segments always sum to `reached` exactly — a bar whose
  // parts do not add up to its whole is the fastest way to lose a reader.
  const undecided = Math.max(reached - supporters - opposed, 0)

  const segments = [
    { key: 'support', label: 'Support', value: supporters, color: 'var(--color-support)' },
    { key: 'undecided', label: 'Undecided', value: undecided, color: 'var(--color-neutral-stance)' },
    { key: 'oppose', label: 'Opposed', value: opposed, color: 'var(--color-oppose)' },
  ].filter((s) => s.value > 0)

  const pct = (value: number) => (reached > 0 ? (value / reached) * 100 : 0)

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Progress</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Where the campaign stands, and how much has been done.
          </p>
        </div>
      </div>

      {totals.isPending ? (
        <p className="mt-6 text-sm text-ink-muted">Loading…</p>
      ) : people === 0 ? (
        <p className="mt-6 rounded-lg border border-line bg-surface-sunken px-4 py-6 text-sm text-ink-muted">
          Nobody has been contacted on this campaign yet. Cut a list, walk or call it, then key the
          results in from <strong className="font-semibold text-ink">Data entry</strong>.
        </p>
      ) : (
        <>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat value={people} label="People contacted" hint="attempted at least once" />
            <Stat
              value={reached}
              label="Actually reached"
              hint={`${people > 0 ? Math.round((reached / people) * 100) : 0}% of attempts spoke to someone`}
            />
            <Stat value={supporters} label="Supporters" hint="latest answer was supportive" />
            <Stat
              value={n(t?.events)}
              label="Contacts recorded"
              hint={`${fmt(n(t?.canvassers))} canvasser${n(t?.canvassers) === 1 ? '' : 's'} credited`}
            />
          </div>

          {/*
            Polarity, as one diverging stacked bar centred on the neutral. The
            title names what it is, so no legend box is needed beyond the direct
            labels below — which are mandatory here anyway: the midpoint grey
            sits under 3:1 on the surface and the validator obligates relief.
          */}
          <section className="mt-8">
            <h2 className="text-sm font-semibold">Where the {fmt(reached)} we reached stand</h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              Each person counted once, at their most recent answer.
            </p>

            {reached === 0 ? (
              <p className="mt-3 text-sm text-ink-muted">
                Nobody has been reached yet — every contact so far was an attempt.
              </p>
            ) : (
              <>
                {/* 2px surface gaps between segments, per the mark spec: the gap
                    is what keeps two fills from reading as one. */}
                <div className="mt-3 flex h-9 w-full gap-[2px] overflow-hidden rounded">
                  {segments.map((s) => (
                    <div
                      key={s.key}
                      style={{ width: `${pct(s.value)}%`, background: s.color }}
                      className="first:rounded-l last:rounded-r"
                      title={`${s.label}: ${fmt(s.value)} (${Math.round(pct(s.value))}%)`}
                    />
                  ))}
                </div>

                <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-1">
                  {segments.map((s) => (
                    <li key={s.key} className="flex items-baseline gap-2 text-sm">
                      <span
                        aria-hidden
                        className="inline-block h-2.5 w-2.5 shrink-0 translate-y-px rounded-sm"
                        style={{ background: s.color }}
                      />
                      {/* Text wears ink tokens, never the series colour — the
                          swatch beside it carries the identity. */}
                      <span className="text-ink">{s.label}</span>
                      <span className="font-semibold tabular-nums text-ink">{fmt(s.value)}</span>
                      <span className="text-xs text-ink-muted">{Math.round(pct(s.value))}%</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          {/* The table view the contrast warning obligates, and the place the
              five-level scale and every not-reached reason actually live. */}
          <h2 className="mt-8 text-sm font-semibold tracking-wide text-ink-muted uppercase">
            Every outcome
          </h2>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[32rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs tracking-wide text-ink-muted uppercase">
                  <th scope="col" className="py-2 pr-4 font-semibold">Outcome</th>
                  <th scope="col" className="py-2 pr-4 font-semibold">Reached?</th>
                  <th scope="col" className="py-2 pr-4 text-right font-semibold">People</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.disposition ?? 'none'} className="border-b border-line/60">
                    <td className="py-2 pr-4">
                      {r.label ?? r.disposition ?? 'Not recorded'}
                      {r.support_level !== null ? (
                        <span className="ml-2 text-xs text-ink-muted tabular-nums">
                          {r.support_level > 0 ? `+${r.support_level}` : r.support_level}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-4 text-ink-muted">
                      {r.is_contact ? 'reached' : 'attempt only'}
                    </td>
                    <td className="py-2 pr-4 text-right font-mono tabular-nums">
                      {fmt(n(r.people))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 grid gap-8 lg:grid-cols-2">
            <div>
              <h2 className="text-sm font-semibold tracking-wide text-ink-muted uppercase">
                By method
              </h2>
              <table className="mt-2 w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs tracking-wide text-ink-muted uppercase">
                    <th scope="col" className="py-2 pr-4 font-semibold">How</th>
                    <th scope="col" className="py-2 pr-4 text-right font-semibold">Contacts</th>
                    <th scope="col" className="py-2 text-right font-semibold">Reached</th>
                  </tr>
                </thead>
                <tbody>
                  {(methods.data?.methods ?? []).map((m) => (
                    <tr key={m.method} className="border-b border-line/60">
                      <td className="py-2 pr-4">{METHOD_LABELS[m.method] ?? m.method}</td>
                      <td className="py-2 pr-4 text-right font-mono tabular-nums">{fmt(n(m.events))}</td>
                      <td className="py-2 text-right font-mono tabular-nums">{fmt(n(m.reached))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <h2 className="text-sm font-semibold tracking-wide text-ink-muted uppercase">
                By canvasser
              </h2>
              <p className="mt-0.5 text-xs text-ink-muted">
                Output, not standing — an attempt counts as work done.
              </p>
              <table className="mt-2 w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs tracking-wide text-ink-muted uppercase">
                    <th scope="col" className="py-2 pr-4 font-semibold">Who</th>
                    <th scope="col" className="py-2 pr-4 text-right font-semibold">Contacts</th>
                    <th scope="col" className="py-2 pr-4 text-right font-semibold">Reached</th>
                    <th scope="col" className="py-2 text-right font-semibold">Support</th>
                  </tr>
                </thead>
                <tbody>
                  {(canvassers.data?.canvassers ?? []).map((c) => (
                    <tr key={c.canvasser} className="border-b border-line/60">
                      <td className="py-2 pr-4">{c.canvasser}</td>
                      <td className="py-2 pr-4 text-right font-mono tabular-nums">{fmt(n(c.events))}</td>
                      <td className="py-2 pr-4 text-right font-mono tabular-nums">{fmt(n(c.reached))}</td>
                      <td className="py-2 text-right font-mono tabular-nums">{fmt(n(c.supporters))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
